import { create, devtools, persist } from 'lib/zustand';

type AuthActions = {
  reset: () => void;
  setInitAuth(isAuthenticated: boolean): void;
  setIsAuthenticated: (isAuthenticated: boolean) => void;
};

type AuthState = {
  isAuthenticated: boolean;
};

const initialState: AuthState = {
  isAuthenticated: false,
};

export function getPersistedSessionFromPersistRoot(persistRootJson: null | string): boolean {
  if (!persistRootJson) {
    return false;
  }

  try {
    const root: unknown = JSON.parse(persistRootJson);

    if (isRecord(root) && 'session' in root && typeof root.session === 'string') {
      const session: unknown = JSON.parse(root.session);

      if (isRecord(session) && 'authenticated' in session && typeof session.authenticated === 'boolean') {
        return session.authenticated;
      }
    }
  } catch {}

  return false;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

const useAuthStore = create<AuthActions & AuthState>()(
  devtools(
    persist(
      (set) => ({
        ...initialState,
        reset() {
          set(initialState);
        },
        setInitAuth(isAuthenticated) {
          set({ isAuthenticated });
        },
        setIsAuthenticated(isAuthenticated) {
          set({ isAuthenticated });
        },
      }),
      {
        name: 'auth-session',
        // TODO: remove after 3.2.0
        onRehydrateStorage(state) {
          return () => {
            const wasInited = localStorage.getItem('auth-session--inited');

            if (!wasInited) {
              const sessionJson = localStorage.getItem('persist:root');

              const isAuthenticated = getPersistedSessionFromPersistRoot(sessionJson);
              state.setInitAuth(isAuthenticated);

              localStorage.setItem('auth-session--inited', 'true');
            }
          };
        },
      },
    ),
  ),
);

export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated);
export const useSetIsAuthenticated = () => useAuthStore((state) => state.setIsAuthenticated);
